import { Select as SelectPrimitive } from 'radix-ui';
import * as React from 'react';

import { Icon } from './Icon';
import { useShadowRoot } from './ShadowRootContext';
import { cn } from './utils/cn';

// ─── Context ───────────────────────────────────────────────────────────

interface SelectContextValue {
  hasError: boolean;
}

const SelectContext = React.createContext<SelectContextValue>({
  hasError: false,
});

// ─── Root ───────────────────────────────────────────────────────────────

function Select({
  hasError = false,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root> & {
  hasError?: boolean;
}) {
  const contextValue = React.useMemo(() => ({ hasError }), [hasError]);
  return (
    <SelectContext.Provider value={contextValue}>
      <SelectPrimitive.Root data-slot="select" {...props} />
    </SelectContext.Provider>
  );
}

// ─── Trigger ────────────────────────────────────────────────────────────

type SelectTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> & {
  size?: 'md' | 'lg';
  surface?: 'filled' | 'transparent';
};

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, size = 'md', surface = 'filled', ...props }, ref) => {
  const { hasError } = React.useContext(SelectContext);
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        // Layout
        'flex w-full items-center justify-between whitespace-nowrap',
        // Size (height, padding, font, radius, gap)
        size === 'lg'
          ? 'h-12 gap-3 rounded-lg px-4 text-sm'
          : 'h-10 gap-2.5 rounded-md px-2.5 text-sm',
        // Border + background
        'border border-border-primary',
        surface === 'filled' ? 'bg-bg-primary' : 'bg-transparent',
        // Typography
        'text-text-secondary',
        // Transitions
        'transition-colors outline-none',
        // Placeholder
        'data-placeholder:text-text-tertiary',
        // Focus
        'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
        // Disabled
        'disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50',
        // Invalid (aria-invalid + legacy hasError)
        'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
        hasError &&
          'border-border-negative-strong text-text-negative focus:ring-border-negative-strong',
        // Dark mode
        'dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:ring-destructive/40',
        // Icon sizing
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-text-tertiary",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <Icon name="ChevronDown" className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

// ─── Content ────────────────────────────────────────────────────────────

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    usesPortal?: boolean;
  }
>(({ className, children, position = 'popper', usesPortal, ...props }, ref) => {
  const { container } = useShadowRoot();
  const content = (
    <SelectPrimitive.Content
      ref={ref}
      data-slot="select-content"
      className={cn(
        'relative z-50 max-h-(--radix-select-content-available-height) min-w-32 overflow-x-hidden overflow-y-auto rounded-md border bg-bg-primary text-text-primary shadow-md',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      collisionBoundary={container}
      collisionPadding={container ? { top: 50 } : undefined}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'w-full min-w-(--radix-select-trigger-width) scroll-my-1',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  );

  if (usesPortal === false) {
    return content;
  }

  return <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal>;
});
SelectContent.displayName = 'SelectContent';

// ─── Group ──────────────────────────────────────────────────────────────

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

// ─── Value ──────────────────────────────────────────────────────────────

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

// ─── Label ──────────────────────────────────────────────────────────────

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => {
  const { hasError } = React.useContext(SelectContext);
  return (
    <SelectPrimitive.Label
      ref={ref}
      data-slot="select-label"
      className={cn(
        'px-2 py-1.5 text-2xs leading-[1.42] font-bold tracking-[0.55px] text-text-tertiary uppercase',
        hasError && 'text-text-negative-strong',
        className,
      )}
      {...props}
    />
  );
});
SelectLabel.displayName = 'SelectLabel';

// ─── Item ───────────────────────────────────────────────────────────────

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none',
      'focus:bg-border-accent-primary focus:text-text-primary',
      'data-disabled:pointer-events-none data-disabled:opacity-50',
      "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-text-tertiary",
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Icon name="Check" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';

// ─── Separator ──────────────────────────────────────────────────────────

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    data-slot="select-separator"
    className={cn('-mx-1 my-1 h-px bg-bg-quaternary', className)}
    {...props}
  />
));
SelectSeparator.displayName = 'SelectSeparator';

// ─── Scroll buttons ─────────────────────────────────────────────────────

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      {...props}
    >
      <Icon name="ChevronUp" className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      {...props}
    >
      <Icon name="ChevronDown" className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

// ─── Exports ────────────────────────────────────────────────────────────

export {
  Select,
  SelectContent,
  SelectContext,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
